/**
 * What a screen talks to, and the only module in `src/data/` it imports.
 *
 * Reads are synchronous, off a map hydrated once when the store opens. A screen
 * that had to await a read would flicker on every navigation, and the mirror is
 * the durability layer rather than the read path.
 *
 * Writes return immediately too: they update the map, tell whoever is watching,
 * commit the row and its mutation to IndexedDB, and kick a drain. Nothing waits
 * for the server, because on a phone in a lift there is no server to wait for.
 */

import { generateKeyBetween } from "fractional-indexing";
import {
  LIST_KINDS,
  inDisplayOrder,
  type Anchors,
  type Creator,
  type Item,
  type ItemInput,
  type ItemPatch,
  type List,
  type ListKind,
  type ListOrder,
  type ListPatch,
} from "../lib/api.js";
import { deleteDatabase, openDatabase } from "./db.js";
import { uuidv7 } from "./ids.js";
import { commitLocal, commitImport, discardImport, markUnsent, openGroup, queueAddedRemoval, readImports, readMeta, retireLegacyOrders, settle, readPending, readSnapshot, releaseInflight, type AddedList, type RowWrite } from "./mirror.js";
export type { AddedList } from "./mirror.js";
import { createHistory, type DetailView, type HistoryView } from "./activity.js";
import type { HistoryTarget } from "./history.js";
import { MAX_GROUP_CHILDREN } from "../../api/src/shared/activity.js";
import { categoryFills, categoryOnAdd } from "../../api/src/shared/grocery/index.js";
import type { ImportRecord, ImportMutation, RecoveryDraft } from "./imports.js";
import { parseExchange } from "../lib/import.js";
import { captureExchange } from "../lib/exchange.js";
import { newSequenceKeys } from "../lib/reorder.js";
export type { RecoveryDraft } from "./imports.js";
import { compact, deviceClock, type Draft, type Fields } from "./queue.js";
import { attachPoller, attachTriggers, createEngine, type EndReason, type HoldReason, type SyncStatus } from "./sync.js";
import { rememberSharedScope, forgetSharedScope, registeredScopes, scopeDatabaseName, scopeKey, type SharedScope, type StoreScope } from "./scopes.js";
import { transportFor, type ReplyScope } from "./transport.js";
import { describeUnsent, type UnsentLine } from "./unsent.js";
import type { Listed, ShareMode, ShareVisitor } from "../lib/sharing.js";
import { editOccurrence, occurrence, type OccurrenceCommand } from "../lib/occurrence.js";

/** How one row of a Clear completed went: removed and saved, removed and not saved, or left alone because it had changed. */
export interface ClearedRow { id: string; saved: boolean; changed: boolean }

/**
 * What ties an Undo move to the move it puts back (g108). `made` is told the
 * operation id a move was queued under, once this device holds it and only
 * where it was queued as a whole action, which is the only kind the server
 * records under that id. `reverses` is that id, given back by the undo.
 */
export interface MoveLink {
  reverses?: string;
  made?(operationId: string): void;
}

/**
 * `loading` and `ready` both show an empty screen when the account is empty —
 * the difference is whether that emptiness is a fact or a gap. Telling a
 * returning user on a new device that they have no lists reads as data loss.
 */
export type Status = "loading" | "ready" | "unreachable";

/**
 * What a blank list name is refused with. Exported so the New list popup can
 * say it beside the field before the throw, in the store's own words.
 */
export const EMPTY_LIST_NAME = "A list needs a name.";

/**
 * Whether anything this device wrote is still waiting to go out.
 *
 * `unsaved` is the third one because a local write can fail — a full disk, a
 * private window, a database the browser threw away — and "Saved on this
 * device" would then be false about the one thing this axis exists to answer.
 * It is not a variety of `pending`: a pending change is safe and waiting, and
 * this one is neither.
 *
 * `writing` is the moment before `pending` (g67): the screen shows the change,
 * and IndexedDB does not have it yet. Saying "Saved on this device" then was a
 * claim a reload in that moment could make false, and one the app's own update
 * reload once did. It is unsent like `pending`, and not yet safe.
 */
export type SyncQueue = "empty" | "writing" | "pending" | "unsaved";

/**
 * How the last attempt went. Deliberately not "online": `navigator.onLine`
 * reports the link rather than reachability, and a request that succeeded a
 * minute ago says nothing about now. Every value here is the outcome of a real
 * request this device made.
 */
export type SyncLink = "ok" | "unreachable" | "failed" | "signed-out" | "held" | "ended";

/**
 * Two independent facts, not one enum of their pairings.
 *
 * `queue` is what the person is actually asking about — is my typing safe — and
 * `link` is why it has not gone yet. Flattening them into one list of states
 * forces a choice about which half to say, and the offline-with-changes case is
 * where that choice loses the more useful half.
 */
export interface SyncState {
  queue: SyncQueue;
  link: SyncLink;
}

export interface ListChange {
  id: string;
  list: List | undefined;
}

export interface ItemChange {
  id: string;
  item: Item | undefined;
}

/** How switching a list's ordering ended on this device (g81). */
export interface ListOrderResult {
  /** `unchanged`: it was already so. `unsaved`: the device could not store it, and the screen is as it was. */
  status: "saved" | "unchanged" | "not_found" | "unsaved";
  /** The blank categories this switch filled. */
  filled: number;
}

/**
 * A write a shared scope does not allow (g71): any change to the list itself,
 * any write through a view link or before the visitor has a name, and any
 * write once the link has stopped working or while the scope is held. Thrown
 * before anything is shown, stored or queued. The page's controls should
 * never offer these; this is what holds if one does.
 */
export class ScopeWriteRefused extends Error {
  constructor(readonly reason: "list_op" | "other_list" | "view" | "name" | "held" | "ended" | "not_writable") {
    super("This shared list cannot take that change.");
    this.name = "ScopeWriteRefused";
  }
}

export interface Store {
  importList(source: string, retryId?: string): Promise<List>;
  recoveries(): RecoveryDraft[];
  onRecoveries(fn: () => void): () => void;
  discardRecovery(id: string): Promise<void>;
  status(): Status;
  /**
   * A 401 has parked the queue. Nothing is discarded and nothing drains until
   * the person signs in again — and on a device that already holds data
   * `status()` still reads `ready`, because reading works perfectly well while
   * signed out. So this is asked separately: without it, writes would pile up
   * behind an expired session with nothing on screen to say so.
   */
  paused(): boolean;
  /**
   * The honest version of the two above, and the one a screen should render.
   *
   * `status()` and `paused()` are derived from this rather than computed beside
   * it, so there is exactly one answer to "what is the sync doing" and no
   * second copy to drift from.
   */
  syncState(): SyncState;

  lists(): List[];
  list(id: string): List | undefined;
  items(listId: string): Item[];
  item(id: string): Item | undefined;
  /**
   * Every item this device holds, in no particular order.
   *
   * For the readers that are not a list: the rail's per-list counts, and the
   * Today and Upcoming projections. Sorting is deliberately not done here —
   * a cross-list reader groups before it orders, and `items(listId)` is what
   * knows a single list's display order.
   */
  allItems(): Item[];
  /**
   * Who made a list or item (g68), by the row's `creatorId`: their chosen name,
   * or null for none or a deleted account. Undefined for an id this device has
   * not been told of. Never an email.
   */
  creator(id: string): Creator | undefined;
  /** A creator's name arrived or changed; rows naming them should re-read it. */
  onCreators(fn: (creator: Creator) => void): () => void;

  onStatus(fn: (status: Status) => void): () => void;
  onPaused(fn: (paused: boolean) => void): () => void;
  onSyncState(fn: (state: SyncState) => void): () => void;
  onLists(fn: (change: ListChange) => void): () => void;
  onItems(listId: string, fn: (change: ItemChange) => void): () => void;
  /**
   * Item changes across every list, for the readers that span lists.
   *
   * The alternative — a cross-list reader subscribing to `onItems` once per
   * list and re-subscribing whenever `onLists` fires — re-derives membership
   * in the view and churns subscriptions on every list change, which is a leak
   * waiting for the one path that forgets to unsubscribe.
   */
  onAllItems(fn: (change: ItemChange) => void): () => void;
  onProblem(fn: (message: string) => void): () => void;

  createList(input: { name: string; kind?: ListKind }): List;
  patchList(id: string, patch: ListPatch): Promise<boolean>;
  removeList(id: string): void;
  /**
   * `anchors` says where the new row goes, by naming its neighbours. Omitted,
   * it appends -- which is what every ordinary capture does.
   *
   * A blank category on a Shopping list is filled with the item's aisle (g82),
   * here and not on the server, so the row shows it at once and offline.
   * `keepBlankCategory` says the blank was chosen: the Add form passes it once
   * the person has typed in the Category field or cleared it.
   */
  createItem(listId: string, input: ItemInput, anchors?: Anchors, options?: { keepBlankCategory?: boolean }): Item;
  patchItem(id: string, patch: ItemPatch): void;
  completeItem(id: string, context?: { at: number; timeZone: string }): void;
  undoCompletion(id: string): void;
  /**
   * Moves a row to the position its neighbours describe.
   *
   * Separate from `patchItem` all the way down to the wire: a patch resolves
   * per field by arrival order, which is right for text and wrong for position.
   *
   * Answers with the local save (g102): true once this device holds the move,
   * false when it could not be written, and nothing at all when the row is
   * not here to move. A refused write still throws.
   *
   * `link` ties an Undo move to the move it puts back (g108, `MoveLink`).
   */
  moveItem(id: string, anchors: Anchors, link?: MoveLink): Promise<boolean> | undefined;
  moveList(id: string, anchors: Anchors, link?: MoveLink): Promise<boolean> | undefined;
  /**
   * Switches Order by category on or off for a list (g81), at once and
   * offline, and queues the change on its own. Nothing is moved: the list is
   * shown by category while it is on, and no order key changes either way.
   * Switching it on fills each blank category on a Shopping list that the
   * shared classifier places, as the server will, and `filled` says how many.
   */
  setListOrder(listId: string, orderBy: ListOrder | null): Promise<ListOrderResult>;
  /** Resolves true only after the local removal and its queued mutation are durable. */
  removeItem(id: string): Promise<boolean>;
  /**
   * Clear completed (g108): removes exactly the rows the person confirmed,
   * as one group of separate removals. `ids` is the confirmed set, and it is
   * the group's manifest: the group, its size and every child's operation id
   * are fixed here, before anything is written, and never taken from a later
   * look at what is ticked. A row that is no longer in the list and ticked is
   * left alone and answered as `changed`. Each removal is still its own
   * write, saved or not on its own. Every row is taken off the screen before
   * this returns.
   */
  clearCompleted(listId: string, ids: readonly string[]): Promise<ClearedRow>[];

  sync(): Promise<void>;
  resume(): void;
  /**
   * The list is on screen (g71). While any lease on a list is held, and that
   * list may change without this device doing anything, the store asks the
   * server every ten seconds or so: always for a shared scope, and for an
   * account only while the list's view or edit link works. Returns the release.
   */
  watchList(listId: string): () => void;
  /** Whether that refresh is on now: a timer waiting, or a refresh running. */
  refreshing(): boolean;
  /**
   * Whether every local write this store has begun is in IndexedDB (g67).
   * Synchronous, so a gate can check it and act in the same task: an update's
   * reload with a write still outstanding loses that write. False too while a
   * write has failed (`unsaved`) and no later one has landed: that edit is
   * only on screen, and a reload would lose it. True once the store is closed.
   */
  stored(): boolean;
  /**
   * Resolves once `stored()` is true: the write chain has drained, writes
   * begun while it waits included, and no failed write is left unrepaired.
   */
  whenStored(): Promise<void>;
  /**
   * Stop syncing, let go of every watcher and close the connection — and leave
   * the data alone. For handing this device to a different account without
   * throwing away the queue this one has not managed to send yet.
   */
  close(): Promise<void>;
  /** Sign-out: everything `close` does, and then delete the database. */
  destroy(): Promise<void>;

  /**
   * Whether the owner's history can be read (g110): the last owner reply
   * carried its stamp. Never for a shared scope, which reads no history.
   */
  activityAvailable(): boolean;
  onActivityAvailable(fn: (available: boolean) => void): () => void;
  /** One series of history as held now: the account's, a list's or an item's. */
  activity(target: HistoryTarget): HistoryView;
  /**
   * A screen shows that series: what the device keeps comes first, then one
   * sync and a read of whatever is newer. No timer. Returns the release.
   */
  watchActivity(target: HistoryTarget, fn: (view: HistoryView) => void): () => void;
  /** Reads the next older page of a series. */
  moreActivity(target: HistoryTarget): Promise<void>;
  activityDetail(actionId: string): DetailView | null;
  /** An action's detail, or with `more` its next page of changes. */
  loadActivityDetail(actionId: string, more?: boolean): Promise<void>;
  onActivityDetail(actionId: string, fn: (view: DetailView) => void): () => void;
  /** Drops the history this device keeps for the account, as when another account takes the device. */
  forgetActivity(): Promise<void>;
  /**
   * The shared lists the account added to its Lists (g132), as its last reply
   * said them, less any removed on this device. A shared scope has none.
   */
  addedLists(): AddedList[];
  onAddedLists(fn: (entries: AddedList[]) => void): () => void;
  /**
   * Takes a shared list out of the account's Lists: gone from this device at
   * once, and from the account with the next push. The owner's list and link
   * are untouched. This device's copy of it goes too, unless something in it
   * was never sent.
   */
  removeAddedList(grantId: string): Promise<void>;
}

/** A shared list's state as its page needs it (g71). Never the link itself. */
export interface ShareState {
  listId: string;
  permission: ShareMode;
  /** An edit link, a named visitor, a sign-in that has not expired, and not held or ended. */
  writable: boolean;
  /** Who the server last said is looking. Null until the first answer. */
  visitor: ShareVisitor | null;
  state: "live" | "held" | "ended";
  reason?: HoldReason | EndReason;
  /** Whether the list is in the visitor's Lists, as the server last said (g132). */
  listed?: Listed;
}

/**
 * One shared list through one link, as one visitor sees it (g71): the same
 * store a screen already knows, whose list writes are refused and whose item
 * writes are refused unless the link can write.
 */
export interface SharedStore extends Store {
  share(): ShareState;
  onShare(fn: (state: ShareState) => void): () => void;
  /** Items with changes that were never sent, because the link stopped working first. */
  unsent(): ReadonlySet<string>;
  /** The same items as the visitor can read them back: text and what did not go (g74). */
  unsentChanges(): UnsentLine[];
  /** Closes the store, deletes its database and forgets the scope. */
  discard(): Promise<void>;
}

/**
 * Every store this page has open or is still closing, for the update gate
 * (g67): its reload waits until each has stored what it was given. Shared
 * stores join it too (g71). A store leaves once its close has finished, and a
 * closed store has nothing left to store.
 */
const openStores = new Set<Store>();
/** The databases those stores hold, so a prune never deletes one this page is showing. */
const openNames = new Set<string>();

export function pageStores(): Store[] {
  return [...openStores];
}

/** An account's store. The shape of this call is what it was before scopes. */
export function openStore(userId: string): Promise<Store> {
  return openScoped({ kind: "account", userId });
}

/** One shared list through one link for one visitor (g71). See `scopes.ts`. */
export async function openSharedStore(scope: SharedScope): Promise<SharedStore> {
  rememberSharedScope(scope);
  const store = (await openScoped(scope)) as SharedStore;
  void pruneSharedScopes(scope).catch(() => undefined);
  return store;
}

/** How long a copy of a list, through a link, is kept once another has replaced it. */
export const SHARED_KEEP_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Clears copies of this list that another has replaced (g71): the same list
 * and link mode under an older generation, or as another visitor. Only a copy
 * with nothing unsent, and only once its link has ended or it has gone unused
 * for `SHARED_KEEP_MS`: another tab may still be showing it, and deleting a
 * database another tab holds closes it under that tab. What is unsent stays
 * until the person discards it.
 */
export async function pruneSharedScopes(current: SharedScope, now = Date.now()): Promise<string[]> {
  const keep = scopeDatabaseName(current);
  const cleared: string[] = [];
  for (const entry of registeredScopes()) {
    if (entry.db === keep || entry.listId !== current.listId || entry.mode !== current.mode) continue;
    if (openNames.has(entry.db)) continue;
    // Opened only to read and delete: no request goes out under this credential.
    const scope: SharedScope = { kind: "shared", credential: { kind: "added", grantId: entry.generation }, listId: entry.listId, mode: entry.mode,
      generation: entry.generation, visitor: entry.visitor, visitorKind: entry.visitorKind };
    const db = await openDatabase(scope);
    let clear = false;
    try {
      const [pending, meta] = await Promise.all([readPending(db), readMeta(db)]);
      clear = pending.length === 0 && (meta.ended !== undefined || now - entry.lastUsed > SHARED_KEEP_MS);
    } finally {
      db.close();
    }
    if (!clear) continue;
    await deleteDatabase(scope).catch(() => undefined);
    forgetSharedScope(entry.db);
    cleared.push(entry.db);
  }
  return cleared;
}

/**
 * Clears this device's copies of a list the account took out of its Lists
 * (g132): the account's copies of that grant, with nothing unsent, that no tab
 * of this page is showing. What is unsent stays until the person discards it,
 * as for any copy a link left behind. Answers the databases it cleared.
 */
export async function forgetAddedCopies(grantId: string): Promise<string[]> {
  const cleared: string[] = [];
  for (const entry of registeredScopes()) {
    if (entry.generation !== grantId || entry.visitorKind !== "account" || openNames.has(entry.db)) continue;
    // Opened only to read and delete: no request goes out under this credential.
    const scope: SharedScope = { kind: "shared", credential: { kind: "added", grantId }, listId: entry.listId, mode: entry.mode,
      generation: entry.generation, visitor: entry.visitor, visitorKind: entry.visitorKind };
    const db = await openDatabase(scope);
    let clear = false;
    try {
      clear = (await readPending(db)).length === 0;
    } finally {
      db.close();
    }
    if (!clear) continue;
    await deleteDatabase(scope).catch(() => undefined);
    forgetSharedScope(entry.db);
    cleared.push(entry.db);
  }
  return cleared;
}

async function openScoped(scope: StoreScope): Promise<Store> {
  const shared = scope.kind === "shared" ? scope : undefined;
  const db = await openDatabase(scope);
  // A tab that died mid-request left records marked as being on the wire. No
  // verdict is coming for them, so they are released before anything else runs.
  await releaseInflight(db);
  // The release before g81 could leave an Auto-order or its Undo queued. This
  // build has no such command: the record is retired before anything reads
  // the queue, and the list is fetched again from the server.
  await retireLegacyOrders(db);
  const snapshot = await readSnapshot(db);
  let imports: ImportRecord[] = await readImports(db);
  const recoveryWatchers = new Set<() => void>();
  // Read once at open so a device that reloads with something queued says so
  // immediately, rather than claiming "Synced" until the first pass gets round
  // to looking. After this the engine owns the flag.
  const pendingAtOpen = await readPending(db);
  const queuedAtOpen = pendingAtOpen.length > 0;
  /** Item records a link ended before sending (g71), kept for the visitor to read back (g74). */
  let unsentRecords = pendingAtOpen.filter(record => record.unsent && record.entity === "item");
  const unsentItems = new Set(unsentRecords.map(record => record.id));

  const lists = new Map(snapshot.lists.map((row) => [row.id, row]));
  const items = new Map(snapshot.items.map((row) => [row.id, row]));
  const creators = new Map(snapshot.creators.map((row) => [row.id, row]));
  const creatorWatchers = new Set<(creator: Creator) => void>();
  /** The account's added lists (g132), and the ones removed here that the server has not yet taken. */
  let added = snapshot.added;
  const removing = new Set(snapshot.removing);
  const addedWatchers = new Set<(entries: AddedList[]) => void>();
  const visibleAdded = (): AddedList[] => added.filter((entry) => !removing.has(entry.grantId));
  const tellAdded = (): void => {
    const entries = visibleAdded();
    for (const watcher of addedWatchers) watcher(entries);
  };

  const listWatchers = new Set<(change: ListChange) => void>();
  const itemWatchers = new Map<string, Set<(change: ItemChange) => void>>();
  const allItemWatchers = new Set<(change: ItemChange) => void>();
  const statusWatchers = new Set<(status: Status) => void>();
  const pausedWatchers = new Set<(paused: boolean) => void>();
  const syncStateWatchers = new Set<(state: SyncState) => void>();
  const problemWatchers = new Set<(message: string) => void>();

  let ready = snapshot.everSynced;
  /** A local write that never reached IndexedDB. See `SyncQueue`. */
  let localFailed = false;
  let syncStatus: SyncStatus = "idle";
  let queued = queuedAtOpen;
  /** Local writes the screen shows and IndexedDB does not have yet. See `SyncQueue`. */
  let writing = 0;
  let committing: Promise<void> = Promise.resolve();
  /** Steps on `committing` not yet finished. Zero means IndexedDB has everything. */
  let links = 0;
  /** `whenStored()` calls waiting for a failed write to be repaired, told when the chain moves. */
  const repairWaiters = new Set<() => void>();
  let closed = false;
  const stored = (): boolean => links === 0 && (!localFailed || closed);
  /** A shared scope's last word from the server, and whether it is waiting or over (g71). */
  let replyScope: ReplyScope | undefined;
  let holdReason: HoldReason | undefined;
  let endReason: EndReason | undefined;
  const shareWatchers = new Set<(state: ShareState) => void>();
  /** Lists on screen, by how many screens hold them (g71). */
  const leases = new Map<string, number>();

  function share(): ShareState {
    const state = endReason ? "ended" : holdReason ? "held" : "live";
    const writable = Boolean(shared && shared.mode === "edit" && shared.visitor !== null && state === "live"
      && (replyScope?.writable ?? true));
    return {
      listId: shared?.listId ?? "",
      permission: shared?.mode ?? "view",
      writable,
      visitor: replyScope?.visitor ?? null,
      state,
      ...(endReason ? { reason: endReason } : holdReason ? { reason: holdReason } : {}),
      ...(replyScope?.listed ? { listed: replyScope.listed } : {}),
    };
  }

  function tellShare(): void {
    const state = share();
    for (const watcher of shareWatchers) watcher(state);
  }

  /**
   * What a shared scope refuses, before anything is shown, stored or queued
   * (g71). The server refuses all of it too; this is so nothing is ever shown
   * as saved that could only be refused.
   */
  function guardWrite(entity: "list" | "item", listId?: string): void {
    if (!shared) return;
    if (entity === "list") throw new ScopeWriteRefused("list_op");
    if (listId !== undefined && listId !== shared.listId) throw new ScopeWriteRefused("other_list");
    if (shared.mode !== "edit") throw new ScopeWriteRefused("view");
    if (endReason) throw new ScopeWriteRefused("ended");
    if (holdReason) throw new ScopeWriteRefused("held");
    if (shared.visitor === null) throw new ScopeWriteRefused("name");
    if (replyScope && !replyScope.writable) throw new ScopeWriteRefused("not_writable");
  }

  /**
   * The one way onto the write chain: each step runs after the one before,
   * and `links` counts the ones not finished, so `stored()` can answer
   * without waiting. A step that fails does not break the chain.
   */
  function chain<T>(step: () => Promise<T>): Promise<T> {
    links += 1;
    for (const wake of repairWaiters) wake();
    repairWaiters.clear();
    const outcome = committing.then(step);
    committing = outcome.then(() => undefined, () => undefined).finally(() => { links -= 1; });
    return outcome;
  }


  async function refreshImports(): Promise<void> {
    imports = await readImports(db);
    for (const entry of imports.filter(entry => entry.status === "rejected")) {
      if (lists.has(entry.list.id)) applyList(entry.list.id, undefined);
      for (const id of entry.originIds) if (items.has(id)) applyItem(id, undefined);
    }
    for (const watch of recoveryWatchers) watch();
  }

  /**
   * The one derivation. `idle` and `syncing` both mean the link is fine — a
   * pass being in flight is not something to tell anybody about, and rendering
   * it faithfully would strobe on every drain.
   */
  function syncState(): SyncState {
    const link: SyncLink =
      syncStatus === "ended"
        ? "ended"
        : syncStatus === "held"
          ? "held"
          : syncStatus === "paused"
        ? "signed-out"
        : syncStatus === "offline"
          ? "unreachable"
          : syncStatus === "failed"
            ? "failed"
            : "ok";
    // `unsaved` outranks the queue flag, which stays raised after a failed
    // write and is right to: something of theirs is still unsent. It is the
    // *claim of durability* that has to be withdrawn, not the claim of unsent.
    const queue: SyncQueue = localFailed ? "unsaved" : writing > 0 ? "writing" : queued ? "pending" : "empty";
    return { queue, link };
  }

  function status(): Status {
    if (ready) return "ready";
    // Nothing downloaded and no prospect of downloading it. A screen says so
    // rather than drawing an empty list it has no reason to believe in.
    return syncState().link === "ok" ? "loading" : "unreachable";
  }

  function paused(): boolean {
    return syncState().link === "signed-out";
  }

  function same(a: SyncState, b: SyncState): boolean {
    return a.queue === b.queue && a.link === b.link;
  }

  function announce(was: Status, wasPaused: boolean, wasSync: SyncState): void {
    const now = status();
    if (now !== was) for (const watcher of statusWatchers) watcher(now);
    if (paused() !== wasPaused) for (const watcher of pausedWatchers) watcher(paused());
    const nowSync = syncState();
    if (!same(nowSync, wasSync)) for (const watcher of syncStateWatchers) watcher(nowSync);
  }

  function tellLists(change: ListChange): void {
    for (const watcher of listWatchers) watcher(change);
  }

  function tellItems(listId: string, change: ItemChange): void {
    const watchers = itemWatchers.get(listId);
    if (watchers) for (const watcher of watchers) watcher(change);
  }

  /**
   * Once per change, not once per list involved.
   *
   * An item that moves between lists is two `tellItems` calls — gone from one,
   * arrived in the other — because a per-list reader has to be told both. A
   * cross-list reader sees one item that changed, and telling it twice would
   * have it delete the row and then draw it again for a move it does not care
   * about.
   */
  function tellAllItems(change: ItemChange): void {
    for (const watcher of allItemWatchers) watcher(change);
  }

  /**
   * An item that moved lists leaves one screen and joins another, and neither
   * view has to know that rule — it is decided here, once.
   */
  function applyItem(id: string, next: Item | undefined): void {
    const previous = items.get(id);
    if (next) items.set(id, next);
    else items.delete(id);

    const from = previous?.listId;
    const to = next?.listId;
    if (from !== undefined && from !== to) tellItems(from, { id, item: undefined });
    if (to !== undefined && next) tellItems(to, { id, item: next });
    tellAllItems({ id, item: next });
  }

  function applyCreator(next: Creator): void {
    creators.set(next.id, next);
    for (const watcher of creatorWatchers) watcher(next);
  }

  function applyList(id: string, next: List | undefined): void {
    if (next) lists.set(id, next);
    else lists.delete(id);
    tellLists({ id, list: next });
  }

  const transport = transportFor(scope);
  /**
   * The owner's history (g110). A shared scope's wire has no history, so it
   * never reads any. Made before the engine, which may speak as it is made.
   */
  const history = createHistory({
    db,
    wire: shared ? undefined : transport.history,
    stamp: snapshot.activity,
    pending: () => readPending(db),
    sync: () => { void engine.sync(); },
  });
  const engine = createEngine({
    db,
    transport,
    actorScope: scopeKey(scope),
    requireSharing: !shared,
    ...(shared ? { expect: { listId: shared.listId, permission: shared.mode, generation: shared.generation, visitor: shared.visitor } } : {}),
    flushLocal: () => committing,
    resolve: (resolutions, delta) => chain(async () => {
      // A whole action the server refused, as it was queued, for the history page's own section (g110).
      const refusals = resolutions.filter((resolution) => resolution.settled?.state === "refused");
      if (refusals.length && history.available()) {
        const records = new Map((await readPending(db)).map((record) => [record.seq, record]));
        history.refused(refusals.flatMap((resolution) => {
          const record = records.get(resolution.seq);
          return record ? [{ record, ...(resolution.settled?.reason ? { reason: resolution.settled.reason } : {}) }] : [];
        }));
      }
      const changes = await settle(db, resolutions, delta);
      await refreshImports();
      return changes;
    }),
    emit: (event) => {
      // A late answer never reaches a closed store's maps or timers (g71).
      if (closed) return;
      if (event.type === "scope") {
        replyScope = event.scope;
        tellShare();
        return;
      }
      if (event.type === "held" || event.type === "ended") {
        if (event.type === "held") holdReason = event.reason;
        else endReason = event.reason;
        if (event.type === "ended") void chain(async () => {
          unsentRecords = (await readPending(db)).filter(record => record.unsent && record.entity === "item");
          for (const record of unsentRecords) unsentItems.add(record.id);
        }).catch(() => undefined).then(tellShare);
        tellShare();
        poller.reevaluate();
        return;
      }
      const was = status();
      const wasPaused = paused();
      const wasSync = syncState();

      if (event.type === "changes") {
        for (const change of event.changes) {
          if (change.entity === "list") applyList(change.id, change.row);
          else if (change.entity === "creator") applyCreator(change.row);
          else if (change.entity === "activity") history.absorb(change.stamp);
          else if (change.entity === "added") {
            added = change.entries;
            // A removal the set no longer holds has been taken. One it still holds was not in this push.
            const held = new Set(added.map((entry) => entry.grantId));
            for (const grantId of removing) if (!held.has(grantId)) removing.delete(grantId);
            tellAdded();
          }
          else applyItem(change.id, change.row);
        }
        history.queueMoved();
      } else if (event.type === "status") {
        syncStatus = event.status;
        // A pass after `resume()` is the scope working again.
        if (event.status === "idle" && holdReason) { holdReason = undefined; tellShare(); }
        // `idle` is the engine saying a pass finished, which is the only thing
        // that turns "not downloaded yet" into "this is the account".
        if (event.status === "idle") ready = true;
      } else if (event.type === "pending") {
        queued = event.pending;
        history.queueMoved();
      } else {
        for (const watcher of problemWatchers) watcher(event.message);
      }

      announce(was, wasPaused, wasSync);
      if (event.type === "status" || (event.type === "changes" && event.changes.some(change => change.entity === "list"))) poller.reevaluate();
    },
  });

  const detach = attachTriggers(engine);
  /**
   * Asks again every ten seconds or so while a list that may change under the
   * person is on screen (g71): every shared scope, and an account's list only
   * while one of its links works. An account's private lists keep the old rule
   * of no timer at all.
   */
  const poller = attachPoller(engine, {
    active: () => {
      if (endReason || closed) return false;
      for (const listId of leases.keys()) {
        if (shared) { if (listId === shared.listId) return true; continue; }
        const sharing = lists.get(listId)?.sharing;
        if (sharing?.view || sharing?.edit) return true;
      }
      return false;
    },
  });
  // Not awaited: a screen should be able to draw the mirror it already has
  // while the first delta is still on its way.
  void engine.sync();

  /**
   * A local write begins: the line says "Saving…" until `settle` runs, and the
   * engine will not call the queue empty in between. Before the write is
   * durable, not after: the mutation exists the moment the screen is told
   * about it, and a queue that is paused will never run a pass to discover it.
   * `settle` runs once, however often it is called, so a watcher that throws
   * into a `.catch` cannot count the write off twice.
   */
  function beginWrite(): (failed: boolean) => void {
    const [was, wasPaused, wasSync] = [status(), paused(), syncState()];
    writing += 1;
    // Its own `pending` emit finds `writing` already outranking it, so the
    // watchers are told once, below.
    engine.queued();
    announce(was, wasPaused, wasSync);
    let settled = false;
    return (failed) => {
      if (settled) return;
      settled = true;
      const [was, wasPaused, wasSync] = [status(), paused(), syncState()];
      writing -= 1;
      engine.durable();
      // A write that lands clears a previous failure: the mirror is working
      // again, and leaving it raised would keep saying so forever.
      localFailed = failed;
      announce(was, wasPaused, wasSync);
      // A history page lists this device's queued changes (g110), and this one is in the queue now.
      history.queueMoved();
    };
  }

  /**
   * Chained rather than awaited. The screen has already been told, and says
   * "Saving…" until IndexedDB has the write (g67) — and the chain gives
   * sign-out and an update's reload something to wait on, so neither closes
   * the database or the page out from under a write.
   */
  function commit(writes: RowWrite[], draft: Draft, made?: (operationId: string) => void): Promise<boolean> {
    // Every write is checked where it is shown, and here again, where it is
    // stored: a path that forgot the first check still cannot queue (g71).
    guardWrite(draft.entity, draft.listId ?? undefined);
    const settle = beginWrite();
    // Captured where the change is committed (g107): an id and the device's
    // time for this one action, and whose queue it is. The mirror keeps them
    // only while the server records.
    draft = { ...draft, captured: draft.captured ?? { operationId: uuidv7(), ...deviceClock() }, actorScope: scopeKey(scope) };

    return chain(async () => {
      try {
        const queued = await commitLocal(db, writes, draft);
        if (queued.operationId !== undefined) made?.(queued.operationId);
        await refreshImports();
        settle(false);
        void engine.sync();
        return true;
      } catch (error: unknown) {
        // Durable or never coming, either way: leaving the count raised would
        // mean no read of the queue could ever report it empty again.
        // The change is in memory and nowhere else. A banner alone is not
        // enough: it is dismissible and the line beside it would go on saying
        // "Saved on this device" about a write that reached nothing, which is
        // the exact claim this whole PR exists to stop making.
        settle(true);
        const message = error instanceof Error ? error.message : "Could not save that locally.";
        for (const watcher of problemWatchers) watcher(message);
        return false;
      }
    });
  }

  async function close(): Promise<void> {
    detach();
    poller.stop();
    engine.stop();
    history.close();
    await committing;
    // Nothing of a closed store is on screen, so nothing of it is lost by a
    // reload: a failed write no longer holds an update.
    closed = true;
    for (const wake of repairWaiters) wake();
    repairWaiters.clear();
    lists.clear();
    items.clear();
    creators.clear();
    creatorWatchers.clear();
    listWatchers.clear();
    itemWatchers.clear();
    allItemWatchers.clear();
    statusWatchers.clear();
    pausedWatchers.clear();
    syncStateWatchers.clear();
    problemWatchers.clear();
    recoveryWatchers.clear();
    shareWatchers.clear();
    leases.clear();
    db.close();
    openStores.delete(store);
    openNames.delete(scopeDatabaseName(scope));
  }

  /**
   * A key for the optimistic render, and only that.
   *
   * The server assigns the real one and sends it back with the next delta;
   * until then the screen needs *something* to sort by, or a row the user just
   * typed would not appear until the network answered. Relative order survives
   * the swap, because the server resolves the same anchors this did.
   *
   * Generated between the same neighbours the mutation names, so an insert in
   * the middle looks right immediately rather than jumping after the first sync.
   */
  function localKey(listId: string | null, anchors: Anchors, excludeId?: string): string {
    // The moving row cannot be its own neighbour: leaving it in would compute
    // bounds around where it already is and generate a key that moves nothing.
    const siblings = inDisplayOrder<List | Item>(listId === null ? [...lists.values()] : itemsIn(listId)).filter((row) => row.id !== excludeId);
    const at = (id: string | null | undefined) =>
      id ? siblings.findIndex((row) => row.id === id) : -1;

    // The nearest neighbour with a different key. Keys can tie for a moment:
    // a provisional key made here and a key the server gave another device's
    // row can match, and no key fits between two equal ones.
    const afterAt = at(anchors.afterId);
    if (afterAt >= 0) {
      const key = siblings[afterAt]!.orderKey;
      return generateKeyBetween(key, siblings.slice(afterAt + 1).find(row => row.orderKey > key)?.orderKey ?? null);
    }

    const beforeAt = at(anchors.beforeId);
    if (beforeAt >= 0) {
      const key = siblings[beforeAt]!.orderKey;
      return generateKeyBetween(siblings.slice(0, beforeAt).reverse().find(row => row.orderKey < key)?.orderKey ?? null, key);
    }

    // No anchors, or neither survives locally: append. Generating from `null`
    // on both sides instead would hand every appended row the same first key.
    return generateKeyBetween(siblings.at(-1)?.orderKey ?? null, null);
  }

  function nextListKey(): string {
    const highest = [...lists.values()]
      .map((row) => row.orderKey)
      .sort()
      .at(-1);
    return generateKeyBetween(highest ?? null, null);
  }

  function itemsIn(listId: string): Item[] {
    return [...items.values()].filter((row) => row.listId === listId);
  }

  /** A list's live items in its normal order. */
  function sequence(listId: string): Item[] {
    return inDisplayOrder(itemsIn(listId));
  }

  /** Puts categories on rows in memory and says so, and returns what they held; the mirror gets them in the caller's transaction. */
  function recategorise(values: Iterable<[string, string | null]>, listId: string): [string, string | null][] {
    const previous: [string, string | null][] = [];
    for (const [id, category] of values) {
      const row = items.get(id);
      if (!row || row.listId !== listId) continue;
      previous.push([id, row.category ?? null]);
      applyItem(id, { ...row, category });
    }
    return previous;
  }

  const categoryWrites = (values: [string, string | null][]): RowWrite[] =>
    values.map(([id, category]) => ({ entity: "item", op: "category", id, category }));

  function changeOccurrence(id: string, kind: "complete" | "undo", context?: { at: number; timeZone: string }): void {
    const before = items.get(id);
    if (!before) return;
    const command: OccurrenceCommand = { operationId: uuidv7(), expectedRevision: before.occurrenceRevision ?? 0,
      ...(kind === "complete" ? context ?? { at: Date.now(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
        : { completionId: before.completion?.id }) };
    const row = { ...occurrence(before, kind, command), updatedAt: Date.now() };
    applyItem(id, row);
    const { operationId, expectedRevision, ...fields } = command;
    commit([{ entity: "item", op: "put", row }], { entity: "item", id, op: kind,
      listId: before.listId, operationId, expectedRevision, ordered: true, fields, before,
      // A completion's action happened when the completion says it did (g107).
      captured: { operationId, ...("at" in command ? { occurredAt: command.at, timeZone: command.timeZone } : deviceClock()) } });
  }

  const store: SharedStore = {
    share,
    onShare(fn) {
      shareWatchers.add(fn);
      return () => shareWatchers.delete(fn);
    },
    unsent: () => unsentItems,
    unsentChanges: () => describeUnsent(unsentRecords, (id) => items.get(id)),
    async discard() {
      await close();
      await deleteDatabase(scope);
      forgetSharedScope(scopeDatabaseName(scope));
    },
    refreshing: () => poller.running(),
    watchList(listId) {
      leases.set(listId, (leases.get(listId) ?? 0) + 1);
      poller.reevaluate();
      let released = false;
      return () => {
        if (released) return;
        released = true;
        const left = (leases.get(listId) ?? 1) - 1;
        if (left > 0) leases.set(listId, left);
        else leases.delete(listId);
        poller.reevaluate();
      };
    },
    importList(source, retryId) {
      guardWrite("list");
      // Parse the entire document at the actual write boundary, not only in the UI.
      const content = parseExchange(source);
      const now = Date.now();
      const list: List = { id: uuidv7(), name: content.name, kind: content.kind, orderKey: nextListKey(),
        createdAt: now, updatedAt: now, deletedAt: null };
      const keys = newSequenceKeys(content.items.length);
      // A file cannot say a blank was chosen, so each blank row of a Shopping
      // list gets its aisle (g82), as the server gives it. Filled here, the
      // rows show it before the import has synced.
      const items = content.items.map((item) => ({ ...item, category: categoryOnAdd(content.kind, item.text, item.category) }));
      const rows: Item[] = items.map((item, index) => ({ ...item, id: uuidv7(), listId: list.id,
        orderKey: keys[index]!, createdAt: now, updatedAt: now, deletedAt: null,
        lastDoneOn: null, occurrenceRevision: 0, completion: null }));
      const mutation: ImportMutation = { ...content, op: "list.import", id: list.id, operationId: uuidv7(),
        items: items.map((item, index) => ({ ...item, id: rows[index]!.id })) };
      const settle = beginWrite();
      return chain(async () => {
        try {
          await commitImport(db, list, rows, mutation, retryId,
            { captured: { operationId: mutation.operationId, ...deviceClock(now) }, actorScope: scopeKey(scope) });
          applyList(list.id, list);
          for (const row of rows) applyItem(row.id, row);
          await refreshImports();
          settle(false);
          void engine.sync();
          return list;
        } catch (error: unknown) { settle(true); throw error; }
      });
    },
    recoveries: () => imports.filter(entry => entry.status === "rejected").map(entry => ({ id: entry.id, revision: entry.revision ?? 0,
      message: entry.message ?? "This import was refused.", content: captureExchange(entry.list, inDisplayOrder(entry.rows ?? [])) })),
    onRecoveries(fn) { recoveryWatchers.add(fn); return () => recoveryWatchers.delete(fn); },
    discardRecovery(id) {
      return chain(async () => {
        await discardImport(db, id);
        await refreshImports();
      });
    },
    status,
    completeItem: (id, context) => { guardWrite("item"); changeOccurrence(id, "complete", context); },
    undoCompletion: id => { guardWrite("item"); changeOccurrence(id, "undo"); },
    paused,
    syncState,

    // String comparison: keys are base-62 and lexicographic. Subtracting them
    // as numbers is the mistake that sorts 'b00' above 'az'.
    lists: () =>
      [...lists.values()].sort((a, b) =>
        a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : 0,
      ),
    list: (id) => lists.get(id),
    // Sorted on read, never on a change. A row that moves the instant it is
    // tapped sends the next tap to whatever slid up into its place.
    items: (listId) => inDisplayOrder(itemsIn(listId)),
    item: (id) => items.get(id),
    allItems: () => [...items.values()],
    creator: (id) => creators.get(id),

    onStatus(fn) {
      statusWatchers.add(fn);
      return () => statusWatchers.delete(fn);
    },
    onPaused(fn) {
      pausedWatchers.add(fn);
      return () => pausedWatchers.delete(fn);
    },
    onSyncState(fn) {
      syncStateWatchers.add(fn);
      return () => syncStateWatchers.delete(fn);
    },
    onLists(fn) {
      listWatchers.add(fn);
      return () => listWatchers.delete(fn);
    },
    onItems(listId, fn) {
      const watchers = itemWatchers.get(listId) ?? new Set();
      watchers.add(fn);
      itemWatchers.set(listId, watchers);
      return () => {
        watchers.delete(fn);
        if (watchers.size === 0) itemWatchers.delete(listId);
      };
    },
    onAllItems(fn) {
      allItemWatchers.add(fn);
      return () => allItemWatchers.delete(fn);
    },
    onCreators(fn) {
      creatorWatchers.add(fn);
      return () => creatorWatchers.delete(fn);
    },
    onProblem(fn) {
      problemWatchers.add(fn);
      return () => problemWatchers.delete(fn);
    },

    activityAvailable: () => history.available(),
    onActivityAvailable: (fn) => history.onAvailable(fn),
    activity: (target) => history.view(target),
    watchActivity: (target, fn) => history.watch(target, fn),
    moreActivity: (target) => history.more(target),
    activityDetail: (actionId) => history.detail(actionId),
    loadActivityDetail: (actionId, more) => history.loadDetail(actionId, more),
    onActivityDetail: (actionId, fn) => history.onDetail(actionId, fn),
    forgetActivity: () => chain(() => history.forget()),
    addedLists: visibleAdded,
    onAddedLists(fn) {
      addedWatchers.add(fn);
      return () => { addedWatchers.delete(fn); };
    },
    async removeAddedList(grantId) {
      if (shared) return;
      removing.add(grantId);
      tellAdded();
      await chain(() => queueAddedRemoval(db, grantId));
      void forgetAddedCopies(grantId).catch(() => undefined);
      void engine.sync();
    },

    createList(input) {
      guardWrite("list");
      const now = Date.now();
      const row: List = {
        id: uuidv7(now),
        kind: input.kind === undefined ? "todo" : aKind(input.kind),
        name: required(input.name, EMPTY_LIST_NAME),
        orderKey: nextListKey(),
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      };

      applyList(row.id, row);
      commit([{ entity: "list", op: "put", row }], {
        entity: "list",
        id: row.id,
        op: "upsert",
        listId: null,
        fields: { ...row },
        before: null,
      });
      return row;
    },

    patchList(id, patch) {
      guardWrite("list");
      const before = lists.get(id);
      if (!before) return Promise.resolve(false);

      const fields = listFields(patch);
      if (Object.keys(fields).length === 0) return Promise.resolve(true);

      const row = { ...before, ...fields, updatedAt: Date.now() } as List;
      applyList(id, row);
      return commit([{ entity: "list", op: "put", row }], {
        entity: "list",
        id,
        op: "patch",
        listId: null,
        fields,
        before,
      });
    },

    removeList(id) {
      guardWrite("list");
      const before = lists.get(id);
      if (!before) return;

      const orphans = itemsIn(id);
      applyList(id, undefined);
      for (const orphan of orphans) applyItem(orphan.id, undefined);

      // The server cascades a list delete to its items' tombstones; doing it
      // here too is what makes the screen agree with the server before the
      // server has been asked.
      commit(
        [
          { entity: "list", op: "remove", id },
          ...orphans.map((row): RowWrite => ({ entity: "item", op: "remove", id: row.id })),
        ],
        { entity: "list", id, op: "delete", listId: null, fields: {}, before },
      );
    },

    createItem(listId, input, anchors = {}, options = {}) {
      guardWrite("item", listId);
      const now = Date.now();
      if (input.repeat && !input.due) throw new Error("A repeating item needs a due date.");
      const text = required(input.text, "An item needs some text.");
      const typed = blankToNull(input.category);
      const row: Item = {
        id: uuidv7(now),
        listId,
        text,
        done: false,
        quantity: blankToNull(input.quantity),
        // One that was typed is kept. A blank one on a Shopping list gets the
        // item's aisle (g82), unless the caller says the blank was chosen. The
        // server stores what this sends and fills nothing for a synced add.
        category: options.keepBlankCategory ? typed : categoryOnAdd(lists.get(listId)?.kind ?? "", text, typed),
        note: blankToNull(input.note),
        due: input.due ?? null,
        ...(input.repeat ? { repeat: input.repeat, repeatAnchor: input.due!.date,
          lastDoneOn: null, occurrenceRevision: 0, completion: null } : {}),
        orderKey: localKey(listId, anchors),
        createdAt: now,
        updatedAt: now,
        deletedAt: null,
      };

      applyItem(row.id, row);
      commit([{ entity: "item", op: "put", row }], {
        entity: "item",
        id: row.id,
        op: "upsert",
        listId,
        operationId: uuidv7(),
        ordered: Boolean(input.repeat),
        // The anchors ride along with the row: `orderKey` in these fields is the
        // provisional local one and is never sent, but the neighbours are what
        // the server needs to work out the real key.
        fields: { ...row, afterId: anchors.afterId ?? null, beforeId: anchors.beforeId ?? null },
        before: null,
      });
      return row;
    },

    moveList(id, anchors, link) {
      guardWrite("list");
      const before = lists.get(id);
      if (!before) return undefined;
      const row: List = { ...before, orderKey: localKey(null, anchors, id), updatedAt: Date.now() };
      applyList(id, row);
      return commit([{ entity: "list", op: "put", row }], {
        entity: "list", id, op: "move", listId: null,
        fields: { afterId: anchors.afterId ?? null, beforeId: anchors.beforeId ?? null }, before,
        ...(link?.reverses !== undefined ? { reverses: link.reverses } : {}),
      }, link?.made);
    },

    moveItem(id, anchors, link) {
      guardWrite("item");
      const before = items.get(id);
      if (!before) return undefined;

      const row: Item = {
        ...before,
        orderKey: localKey(before.listId, anchors, id),
        updatedAt: Date.now(),
      };
      applyItem(id, row);
      return commit([{ entity: "item", op: "put", row }], {
        entity: "item",
        id,
        op: "move",
        listId: before.listId,
        fields: { afterId: anchors.afterId ?? null, beforeId: anchors.beforeId ?? null },
        before,
        ...(link?.reverses !== undefined ? { reverses: link.reverses } : {}),
      }, link?.made);
    },

    patchItem(id, patch) {
      guardWrite("item");
      const before = items.get(id);
      if (!before) return;

      const fields = itemFields(patch);
      if (Object.keys(fields).length === 0) return;

      const row = { ...editOccurrence(before, fields as ItemPatch), updatedAt: Date.now() };
      applyItem(id, row);
      commit([{ entity: "item", op: "put", row }], {
        entity: "item",
        id,
        op: "patch",
        listId: before.listId,
        ...(before.repeat || fields.repeat || before.occurrenceRevision ? {
          operationId: uuidv7(), expectedRevision: before.occurrenceRevision ?? 0, ordered: true,
        } : {}),
        fields,
        before,
      });
    },

    async setListOrder(listId, orderBy) {
      guardWrite("list");
      const before = lists.get(listId);
      if (!before) return { status: "not_found", filled: 0 };
      if ((before.orderBy ?? null) === orderBy) return { status: "unchanged", filled: 0 };

      const row: List = { ...before, orderBy, updatedAt: Date.now() };
      applyList(listId, row);
      // The same labels the server will write when the switch arrives, shown
      // at once. Only on a Shopping list, and only from off to on.
      const fills = orderBy === "category" && before.kind === "shopping" ? categoryFills(sequence(listId)) : [];
      const emptied = recategorise(fills, listId);
      // The rows the fill put in memory, by identity: an edit made while the
      // save is pending replaces the row, and is not this fill's to undo.
      const shown = new Map(fills.map(([id]) => [id, items.get(id)]));
      // A record of its own (`ordered`), after everything queued so far: the
      // server fills only the items it already holds. The fills are local
      // writes with no record; the server makes its own and they come back.
      const saved = await commit([{ entity: "list", op: "put", row }, ...categoryWrites(fills)], {
        entity: "list", id: listId, op: "patch", listId, operationId: uuidv7(), ordered: true,
        fields: { orderBy }, before,
      });
      if (saved) return { status: "saved", filled: fills.length };
      // Never durable: the screen goes back to what the device still holds.
      // A row edited since keeps its edit, even one that typed the same
      // category the fill had put there.
      if (lists.get(listId)) applyList(listId, { ...lists.get(listId)!, orderBy: before.orderBy ?? null });
      recategorise(emptied.filter(([id]) => items.get(id) === shown.get(id)), listId);
      return { status: "unsaved", filled: 0 };
    },

    removeItem(id) {
      guardWrite("item");
      const before = items.get(id);
      if (!before) return Promise.resolve(false);

      applyItem(id, undefined);
      return commit([{ entity: "item", op: "remove", id }], {
        entity: "item",
        id,
        op: "delete",
        listId: before.listId,
        fields: {},
        before,
      });
    },

    clearCompleted(listId, ids) {
      guardWrite("item", listId);
      // Nothing confirmed is no group: a manifest with no child is one the server refuses.
      if (ids.length === 0) return [];
      // Fixed before anything is written: the group, its size and each child's operation.
      const groupId = uuidv7();
      const operations = ids.map(() => uuidv7());
      const clock = deviceClock();
      // More than a list can hold is no group the server would take, so the rows go as separate removals.
      const grouped = ids.length <= MAX_GROUP_CHILDREN;
      // Checked and taken off the screen now, so nothing that happens while the writes run can widen the set.
      const rows = ids.map((id) => {
        const row = items.get(id);
        return row && row.listId === listId && row.done ? row : undefined;
      });
      for (const row of rows) if (row) applyItem(row.id, undefined);

      // The whole manifest is held before its first child is queued.
      if (grouped) void chain(() => openGroup(db, { id: groupId, kind: "clear_completed", operations })).catch(() => false);
      const notQueued: string[] = [];
      const results = ids.map((id, index): Promise<ClearedRow> => {
        const before = rows[index];
        const operationId = operations[index]!;
        if (!before) { notQueued.push(operationId); return Promise.resolve({ id, saved: false, changed: true }); }
        return commit([{ entity: "item", op: "remove", id }], {
          entity: "item", id, op: "delete", listId: before.listId, fields: {}, before,
          captured: { operationId, ...clock },
          ...(grouped ? { group: { id: groupId, index, size: ids.length } } : {}),
        }).then((saved) => {
          if (!saved) notQueued.push(operationId);
          return { id, saved, changed: false };
        });
      });
      // A row left alone or not saved will never be sent, and the group is told so.
      if (grouped) void Promise.all(results).then(() => {
        if (notQueued.length === 0) return;
        return chain(() => markUnsent(db, groupId, notQueued)).then(() => { void engine.sync(); });
      }).catch(() => undefined);
      return results;
    },

    sync: () => engine.sync(),
    resume: () => engine.resume(),

    stored,
    async whenStored() {
      // A write chained while this waited is waited for too, and a failed
      // write holds until a later one lands or the store closes.
      while (!stored()) {
        if (links > 0) await committing;
        else await new Promise<void>((wake) => repairWaiters.add(wake));
      }
    },

    close,

    async destroy() {
      await close();
      await deleteDatabase(scope);
      if (shared) forgetSharedScope(scopeDatabaseName(scope));
    },
  };
  openStores.add(store);
  openNames.add(scopeDatabaseName(scope));
  return store;
}

/**
 * The server refuses an empty string with "use null to clear this", and it
 * refuses the whole request rather than one mutation — so a blank field queued
 * here would take four hundred and ninety-nine good ones down with it.
 */
function blankToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function aKind(value: ListKind | undefined): ListKind {
  if (value === undefined || !LIST_KINDS.includes(value)) {
    throw new Error("That is not a kind of list.");
  }
  return value;
}

function required(value: string, message: string): string {
  const trimmed = value.trim();
  if (trimmed === "") throw new Error(message);
  return trimmed;
}

/**
 * Only the fields the server's patch schema knows, and only when they carry a
 * value.
 *
 * The allowlist is not belt-and-braces over the types. Zod strips a key it does
 * not recognise **before** the "at least one field is required" refinement
 * runs, so a patch carrying nothing but unknown keys arrives as `{}` and is
 * refused — and a schema refusal is a 400 on the whole request rather than a
 * verdict on the one mutation, so it would take up to four hundred and
 * ninety-nine good mutations down with it.
 */
const LIST_PATCH_FIELDS = ["name", "kind", "position"] as const;
const ITEM_PATCH_FIELDS = ["text", "done", "quantity", "category", "note", "due", "repeat", "position"] as const;

function only(patch: object, allowed: readonly string[]): Fields {
  const fields: Fields = {};
  for (const [key, value] of Object.entries(compact(patch))) {
    if (allowed.includes(key)) fields[key] = value;
  }
  return fields;
}

function listFields(patch: ListPatch): Fields {
  const fields = only(patch, LIST_PATCH_FIELDS);
  if ("name" in fields) fields.name = required(patch.name ?? "", EMPTY_LIST_NAME);
  if ("kind" in fields) fields.kind = aKind(patch.kind);
  return fields;
}

function itemFields(patch: ItemPatch): Fields {
  const fields = only(patch, ITEM_PATCH_FIELDS);
  if ("text" in fields) fields.text = required(patch.text ?? "", "An item needs some text.");
  if ("quantity" in fields) fields.quantity = blankToNull(patch.quantity);
  if ("category" in fields) fields.category = blankToNull(patch.category);
  if ("note" in fields) fields.note = blankToNull(patch.note);
  return fields;
}
